/**
 * CNC Wishlist — anon-first state manager + heart toggle handler.
 *
 * v1 ships ANON ONLY (localStorage). Backend persistence comes online when
 * Supabase migration + edge function + Shopify app proxy are deployed
 * (RFC-WISHLIST-001 §3 + §6) — that's a one-line swap in fetchRemote().
 *
 * Pattern: single source of truth in window.cncWishlist (Set of product IDs).
 * All hearts on the page read from this Set on init + on state-change events.
 *
 * Klaviyo events fired on toggle: 'Wishlisted' / 'Wishlist Removed'
 * (RFC-WISHLIST-001 §5; requires Klaviyo on-site script to be loaded —
 * the cutover-prep flip will re-enable that embed).
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'cnc_wishlist_v1';
  var ANON_ID_KEY = 'cnc_wishlist_anon_id_v1';
  var EVENT_CHANGED = 'cnc:wishlist:changed';
  var API_URL = 'https://dkzndqkjjzhmmcebqoib.supabase.co/functions/v1/wishlist-api';

  /* Resolve a persistent customer ID:
     - logged-in customer (window.cncCustomerId set by Liquid in theme.liquid) takes precedence
     - otherwise an anon UUID stored in localStorage; same browser keeps the same ID across sessions
     v2 hardening (post app-proxy): customer_id arrives Shopify-verified via the proxy instead. */
  function genUUID() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
  function getCustomerId() {
    if (window.cncCustomerId) return { id: String(window.cncCustomerId), is_anon: false };
    try {
      var stored = localStorage.getItem(ANON_ID_KEY);
      if (stored) return { id: stored, is_anon: true };
      var fresh = 'anon-' + genUUID();
      localStorage.setItem(ANON_ID_KEY, fresh);
      return { id: fresh, is_anon: true };
    } catch (e) { return { id: 'anon-' + Date.now(), is_anon: true }; }
  }

  function apiPost(body) {
    return fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).catch(function () { /* offline / network — local state still works */ });
  }
  function apiGet(customer_id) {
    return fetch(API_URL + '?customer_id=' + encodeURIComponent(customer_id))
      .then(function (r) { return r.ok ? r.json() : { items: [] }; })
      .catch(function () { return { items: [] }; });
  }

  function loadLocal() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return {};
      var parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) { return {}; }
  }

  function saveLocal(map) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(map)); } catch (e) {}
  }

  /* Public state — keyed by product_id (string) → {handle, title, url, image, price, added_at, source} */
  window.cncWishlist = window.cncWishlist || loadLocal();

  function isWishlisted(pid) {
    return !!window.cncWishlist[String(pid)];
  }

  function add(meta) {
    var pid = String(meta.productId);
    window.cncWishlist[pid] = {
      handle: meta.handle,
      title: meta.title,
      url: meta.url,
      image: meta.image,
      price: meta.price,
      added_at: new Date().toISOString(),
      source: meta.source || 'unknown',
    };
    saveLocal(window.cncWishlist);
    var c = getCustomerId();
    apiPost({ action: 'add', customer_id: c.id, is_anon: c.is_anon, product_id: pid, source: meta.source });
    dispatch(pid, 'added', meta);
  }

  function remove(pid) {
    pid = String(pid);
    var meta = window.cncWishlist[pid];
    delete window.cncWishlist[pid];
    saveLocal(window.cncWishlist);
    var c = getCustomerId();
    apiPost({ action: 'remove', customer_id: c.id, product_id: pid });
    dispatch(pid, 'removed', meta || {});
  }

  function dispatch(pid, action, meta) {
    document.dispatchEvent(new CustomEvent(EVENT_CHANGED, {
      detail: { productId: pid, action: action, meta: meta, count: Object.keys(window.cncWishlist).length },
    }));
    fireKlaviyo(action, meta);
    syncAllHearts();
  }

  function fireKlaviyo(action, meta) {
    /* Klaviyo's on-site script exposes window._learnq.push */
    if (!window._learnq || typeof window._learnq.push !== 'function') return;
    var eventName = action === 'added' ? 'Wishlisted' : 'Wishlist Removed';
    try {
      window._learnq.push(['track', eventName, {
        ProductName: meta.title || '',
        ProductID: meta.productId || '',
        Handle: meta.handle || '',
        URL: meta.url ? (location.origin + meta.url) : '',
        ImageURL: meta.image || '',
        Price: meta.price || '',
        Source: meta.source || '',
      }]);
    } catch (e) {}
  }

  function syncHeart(btn) {
    var pid = btn.getAttribute('data-cnc-wl-product-id');
    var on = isWishlisted(pid);
    btn.classList.toggle('cnc-wl--on', on);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    btn.setAttribute('aria-label', on ? 'Remove from wishlist' : 'Save to wishlist');
  }

  function syncAllHearts() {
    document.querySelectorAll('[data-cnc-wl-toggle]').forEach(syncHeart);
    updateHeaderCount();
  }

  function updateHeaderCount() {
    var n = Object.keys(window.cncWishlist).length;
    document.querySelectorAll('[data-cnc-wl-count]').forEach(function (el) {
      el.textContent = String(n);
      el.classList.toggle('cnc-wl-count--empty', n === 0);
    });
  }

  function onToggleClick(e) {
    var btn = e.target.closest('[data-cnc-wl-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var pid = btn.getAttribute('data-cnc-wl-product-id');
    if (!pid) return;
    if (isWishlisted(pid)) {
      remove(pid);
    } else {
      add({
        productId: pid,
        handle: btn.getAttribute('data-cnc-wl-handle'),
        title: btn.getAttribute('data-cnc-wl-title'),
        url: btn.getAttribute('data-cnc-wl-url'),
        image: btn.getAttribute('data-cnc-wl-image'),
        price: btn.getAttribute('data-cnc-wl-price'),
        source: btn.getAttribute('data-cnc-wl-source'),
      });
      /* Small pulse animation cue */
      btn.classList.add('cnc-wl--pulse');
      setTimeout(function () { btn.classList.remove('cnc-wl--pulse'); }, 600);
    }
  }

  function hydrateFromBackend() {
    var c = getCustomerId();
    /* If user just logged in and we have an anon UUID stored, merge anon → customer first */
    if (!c.is_anon) {
      try {
        var anonId = localStorage.getItem(ANON_ID_KEY);
        if (anonId) {
          apiPost({ action: 'merge', from_anon_id: anonId, to_customer_id: c.id })
            .then(function () { try { localStorage.removeItem(ANON_ID_KEY); } catch (e) {} });
        }
      } catch (e) {}
    }
    /* Pull authoritative server state and merge into local (server wins for items it knows about) */
    apiGet(c.id).then(function (resp) {
      var items = (resp && resp.items) || [];
      var changed = false;
      items.forEach(function (it) {
        if (!window.cncWishlist[it.product_id]) {
          window.cncWishlist[it.product_id] = {
            handle: '', title: '', url: '/products/' + (it.product_id || ''),
            image: '', price: '',
            added_at: it.added_at, source: it.source || 'server',
          };
          changed = true;
        }
      });
      if (changed) {
        saveLocal(window.cncWishlist);
        syncAllHearts();
      }
    });
  }

  function init() {
    document.addEventListener('click', onToggleClick, true);
    syncAllHearts();
    hydrateFromBackend();
    /* Re-sync after Dawn re-renders cards (infinite scroll, facet change).
       CRITICAL: only fire when NEW heart elements are inserted — never on
       attribute or text changes (otherwise syncAllHearts modifies DOM and
       re-triggers this observer = infinite loop = page never finishes load). */
    var pending = false;
    var mo = new MutationObserver(function (mutations) {
      if (pending) return;
      var hasNewHeart = false;
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if (n.matches && n.matches('[data-cnc-wl-toggle]')) { hasNewHeart = true; break; }
          if (n.querySelector && n.querySelector('[data-cnc-wl-toggle]')) { hasNewHeart = true; break; }
        }
        if (hasNewHeart) break;
      }
      if (!hasNewHeart) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        syncAllHearts();
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Public API for /pages/wishlist + header */
  window.cncWishlistAPI = {
    isWishlisted: isWishlisted,
    add: add,
    remove: remove,
    list: function () {
      return Object.keys(window.cncWishlist).map(function (pid) {
        var v = window.cncWishlist[pid];
        return Object.assign({ productId: pid }, v);
      }).sort(function (a, b) { return (b.added_at || '').localeCompare(a.added_at || ''); });
    },
    onChange: function (cb) { document.addEventListener(EVENT_CHANGED, function (e) { cb(e.detail); }); },
  };
})();
